import { Component, Vue, Watch, Model, Prop } from 'vue-property-decorator';
import { Report as ReportModel } from '@/models/Report';
import { ReportLog } from '@/models/ReportLog';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { getStatusColor, getStatusLabel, getStyledStatusLog } from '@/support/ReportStatus';
import { debounce } from 'lodash';

@Component<Communication>({
  components: {
  },
})
export default class Communication extends Vue {
  public $pageTitle = 'Communicatie';

  @Model('report', { default: () => new ReportModel() })
  protected report!: ReportModel;

  @Model('readOnly', { default: false })
  protected readOnly = false;

  @Prop({ default: false })
  protected syncing!: boolean;

  protected isLoading = true;

  protected isLoadingMoreMessage = false;

  // eslint-disable-next-line @typescript-eslint/ban-types
  protected debouncedScroll: Function = debounce(this.scrollLogContainer, 500);

  // Create Message
  protected panel: any = [];

  protected panelVisible = false;

  protected comment = '';

  protected isInternal = true;

  // List ReportLogs
  protected reportLogs: ReportLog[] = [];

  protected page = 1;

  protected pageSize = 0;

  protected panelHeight = 0;

  protected selectedLogTypes: string[] = [];

  protected logTypes: LogType[] = [
    {
      label: 'Aanpassing',
      value: 'change',
    },
    {
      label: 'Bericht',
      value: 'message',
    },
    {
      label: 'Status wijziging',
      value: 'status_change',
    },
  ];

  @Watch('selectedLogTypes', { deep: true })
  protected selectedLogTypesChanged(): void {
    this.reportLogs = [];
    this.page = 1;
    this.fetchReportLogs();
  }

  // Helpers:
  protected planningChangeRegex = /(.*):\n[-]+[\n]{2}(.|\n)*/g;

  public async mounted(): Promise<void> {
    this.isLoading = true;
    await this.fetchReportLogs();

    this.isLoading = false;
    this.isLoadingMoreMessage = false;
    window.addEventListener('scroll', this.scrollEvent);
  }

  protected async fetchReportLogs(): Promise<void> {
    const filter: ReportLogFilter = {
      report: this.$route.params.id,
    };

    if (this.selectedLogTypes && this.selectedLogTypes.length) {
      filter.types = this.selectedLogTypes;
    }

    await new ReportLog()
      .filter(filter)
      .limit(20)
      .page(this.page)
      .sort('created_at', 'DESC')
      .all()
      .then((reportLogs: ReportLog[]) => {
        this.reportLogs = [...this.reportLogs, ...reportLogs];
        if (reportLogs[0]) {
          this.pageSize = (reportLogs[0].meta as any).last_page;
        }

        this.isLoading = false;
        this.isLoadingMoreMessage = false;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected scrollEvent(): void {
    this.debouncedScroll();
  }

  protected scrollLogContainer(): void {
    if (! this.$refs.logContainer) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const container: any = this.$refs.logContainer;
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    const bodyRect = document.body.getBoundingClientRect();
    const elemRect = container.getBoundingClientRect();
    const offset = elemRect.bottom - bodyRect.bottom;

    if (scrollPosition > Math.floor(offset) - 10) {
      if (this.page === 1 && this.pageSize === 1) return;
      if (this.page >= this.pageSize) return;
      this.page += 1;
      this.fetchReportLogs();
    }
  }

  protected sendMessage(comment: string): void {
    const payload = {
      report: this.$route.params.id,
      text: comment,
      is_internal: this.isInternal,
    };

    new ReportLog()
      .create(payload)
      .then(() => {
        this.comment = '';
        this.isLoading = true;
        this.reportLogs = [];
        this.page = 1;
        this.fetchReportLogs();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected togglePanel(): void {
    if (! this.panelVisible) {
      this.panelVisible = true;
      const innerPanel = document.getElementsByClassName('inner-panel');
      this.panelHeight = innerPanel[0].clientHeight;
    } else {
      this.panelVisible = false;
      this.panelHeight = 0;
    }
  }

  // Helpers:
  protected getStatusColor(key: string): string {
    return getStatusColor(key);
  }

  protected getStatusLabel(key: string): string {
    return getStatusLabel(key);
  }

  protected getStyledStatusLog(reportLog: ReportLogFilter): string {
    if (reportLog && reportLog.text) {
      return getStyledStatusLog(reportLog.text, reportLog.type);
    }

    return '';
  }

  protected getReportLogAction(reportLog: ReportLog): string {
    if (! reportLog.text) {
      return '';
    }

    return reportLog.text.replace(this.planningChangeRegex, '');
  }

  protected getReportLogMessage(reportLog: ReportLog): string {
    if (! reportLog.text) {
      return '';
    }

    return (reportLog.text.match(this.planningChangeRegex) || '').toString();
  }
}

interface LogType {
  label: string;
  value: string;
}

export interface ReportLogFilter {
  report: string;
  type?: string;
  types?: string[];
  text?: string;
}
